import { fetchWithAuth } from './auth';

export interface TaxonomyLabel {
  name: string;
  descriptions: string[];
}
export interface TaxonomyCategory {
  name: string;
  labels: TaxonomyLabel[];
}
export interface Taxonomy {
  categories: TaxonomyCategory[];
  version?: string;
}

export async function getTaxonomy(): Promise<Taxonomy> {
  const r = await fetchWithAuth('/api/photos/taxonomy');
  if (!r.ok) throw new Error(`taxonomy fetch failed: ${r.status}`);
  return (await r.json()) as Taxonomy;
}

export async function getDefaultTaxonomy(): Promise<Taxonomy> {
  const r = await fetchWithAuth('/api/photos/taxonomy/default');
  if (!r.ok) throw new Error(`default taxonomy fetch failed: ${r.status}`);
  return (await r.json()) as Taxonomy;
}

async function putTaxonomy(taxonomy: Taxonomy): Promise<Taxonomy> {
  const r = await fetchWithAuth('/api/photos/taxonomy', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(taxonomy),
  });
  if (!r.ok) throw new Error(`taxonomy put failed: ${r.status}: ${await r.text()}`);
  return (await r.json()) as Taxonomy;
}

/** Splice a new label into a category and PUT. Idempotent if label already exists. */
export async function addLabel(category: string, label: string): Promise<Taxonomy> {
  const t = await getTaxonomy();
  const cat = t.categories.find((c) => c.name === category);
  if (!cat) throw new Error(`category not found: ${category}`);
  if (cat.labels.some((l) => l.name === label)) return t;
  cat.labels.push({ name: label, descriptions: [] });
  return putTaxonomy(t);
}

/** Splice a new description into a label and PUT. Idempotent. */
export async function addDescription(
  category: string,
  label: string,
  description: string
): Promise<Taxonomy> {
  const t = await getTaxonomy();
  const cat = t.categories.find((c) => c.name === category);
  if (!cat) throw new Error(`category not found: ${category}`);
  const lab = cat.labels.find((l) => l.name === label);
  if (!lab) throw new Error(`label not found: ${category}/${label}`);
  if (lab.descriptions.includes(description)) return t;
  lab.descriptions.push(description);
  return putTaxonomy(t);
}

/**
 * Compare a current taxonomy against the default and report which items are
 * user-added. Used to render ORG vs MINE chips in the bottom sheet.
 */
export function classifyOrigin(
  current: Taxonomy,
  defaults: Taxonomy
): {
  isUserLabel: (category: string, label: string) => boolean;
  isUserDescription: (category: string, label: string, description: string) => boolean;
} {
  // Reference current to satisfy noUnusedParameters; classification is by default-set membership.
  void current;
  const defaultCats = new Map<string, Map<string, Set<string>>>();
  for (const c of defaults.categories) {
    const labelMap = new Map<string, Set<string>>();
    for (const l of c.labels) labelMap.set(l.name, new Set(l.descriptions));
    defaultCats.set(c.name, labelMap);
  }
  return {
    isUserLabel(cat, label) {
      const lm = defaultCats.get(cat);
      return !lm || !lm.has(label);
    },
    isUserDescription(cat, label, desc) {
      const lm = defaultCats.get(cat);
      const ds = lm?.get(label);
      return !ds || !ds.has(desc);
    },
  };
}
